// src/pages/builder/GameSoundsPanel.js
//
// 🎵 GAME SOUNDS — Layout Builder panel. Upload an MP3 for each moment in
// the games (right answer, wrong answer, questions finished, bonus-round
// diamonds / bombs / power-ups / wrong items, game over) and every game
// plays it. No code change or deploy needed to change a sound.
//
//   Every game     one sound per moment, used by all games
//   One game only  a different sound for one game (e.g. Diamond Drop),
//                  anything left alone uses the "every game" sound
//
// Files go to Storage game-sounds/… (admins only, like the other admin
// folders); the list of what plays where is rewardSettings/main.sounds,
// which the games read live (services/gameSounds.js). Nothing uploaded =
// each game keeps the sound it always had.

import React, { useMemo, useRef, useState } from "react";
import { getDownloadURL, ref as storageRef, uploadBytesResumable } from "firebase/storage";
import { storage } from "../../firebase/firebaseConfig";
import { saveRewardSettings, useRewardSettings } from "../../services/rewardSettingsService";
import { SOUND_GAMES, SOUND_SLOTS, playUrl } from "../../services/gameSounds";
import "./gameSounds.css";

const MAX_MB = 5;
const safeName = (n) =>
  String(n || "sound")
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40) || "sound";

const clone = (x) => JSON.parse(JSON.stringify(x || {}));

const SlotRow = ({ slot, value, inherited, scope, busy, progress, onUpload, onVolume, onNone, onReset }) => {
  const fileRef = useRef(null);
  const set = value && (value.url || value.none);
  const shown = set ? value : inherited; // what actually plays
  const status = value?.none
    ? { cls: "is-none", text: "🔇 No sound" }
    : value?.url
    ? { cls: "is-set", text: `🎵 ${value.name || "Uploaded sound"}` }
    : scope === "all"
    ? { cls: "is-default", text: "Built-in sound" }
    : inherited?.none
    ? { cls: "is-default", text: "Same as every game (no sound)" }
    : inherited?.url
    ? { cls: "is-default", text: `Same as every game (🎵 ${inherited.name || "uploaded"})` }
    : { cls: "is-default", text: "Same as every game (built-in)" };

  return (
    <div className="gs-row">
      <div className="gs-what">
        <b>{slot.label}</b>
        <span className="gs-hint">{slot.hint}</span>
      </div>
      <div className="gs-now">
        <span className={`gs-status ${status.cls}`}>{status.text}</span>
        {value?.url && (
          <label className="gs-vol" title="Volume">
            🔉
            <input type="range" min={0.1} max={1} step={0.05} value={value.volume ?? 1} onChange={(e) => onVolume(Number(e.target.value))} />
            <span>{Math.round((value.volume ?? 1) * 100)}%</span>
          </label>
        )}
      </div>
      <div className="gs-btns">
        <button
          type="button"
          className="lb-btn lb-btn-soft lb-btn-sm"
          disabled={!shown?.url}
          title={shown?.url ? "Play it" : "Built-in sounds play in the game"}
          onClick={() => shown?.url && playUrl(shown.url, shown.volume ?? 1)}
        >
          ▶
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,audio/ogg,.mp3,.wav,.m4a,.ogg"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (f) onUpload(f);
          }}
        />
        <button type="button" className="lb-btn lb-btn-sm" disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? `Uploading ${Math.round((progress || 0) * 100)}%` : "⬆ Upload"}
        </button>
        <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" disabled={busy || value?.none} onClick={onNone} title="Play nothing at this moment">
          🔇
        </button>
        {set && (
          <button type="button" className="lb-btn lb-btn-ghost lb-btn-sm" disabled={busy} onClick={onReset}>
            {scope === "all" ? "↺ Built-in" : "↺ Same as every game"}
          </button>
        )}
      </div>
    </div>
  );
};

const GameSoundsPanel = () => {
  const { settings } = useRewardSettings();
  const sounds = useMemo(() => settings?.sounds || {}, [settings]);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("all"); // "all" | a SOUND_GAMES id
  const [busy, setBusy] = useState(null); // { slot, progress }
  const [msg, setMsg] = useState(null);

  const game = SOUND_GAMES.find((g) => g.id === scope) || null;
  const slots = scope === "all" ? SOUND_SLOTS : SOUND_SLOTS.filter((s) => s.group === game?.group && !s.end);
  const valueOf = (slotId) => (scope === "all" ? sounds.all?.[slotId] : sounds.games?.[scope]?.[slotId]) || null;
  const inheritedOf = (slotId) => (scope === "all" ? null : sounds.all?.[slotId] || null);

  const write = async (slotId, value) => {
    const next = clone(sounds);
    if (scope === "all") {
      next.all = next.all || {};
      if (value) next.all[slotId] = value;
      else delete next.all[slotId];
    } else {
      next.games = next.games || {};
      next.games[scope] = next.games[scope] || {};
      if (value) next.games[scope][slotId] = value;
      else delete next.games[scope][slotId];
      if (!Object.keys(next.games[scope]).length) delete next.games[scope];
    }
    await saveRewardSettings({ sounds: next });
  };

  const save = async (slotId, value, okText) => {
    setMsg(null);
    try {
      await write(slotId, value);
      if (okText) setMsg({ tone: "ok", text: okText });
    } catch (e) {
      setMsg({ tone: "error", text: `Couldn’t save: ${e?.message || e}` });
    }
  };

  const upload = async (slot, file) => {
    setMsg(null);
    if (!/^audio\//.test(file.type) && !/\.(mp3|wav|m4a|ogg)$/i.test(file.name)) {
      setMsg({ tone: "error", text: "That isn’t a sound file. Use an MP3 (or WAV, M4A, OGG)." });
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setMsg({ tone: "error", text: `That file is over ${MAX_MB} MB. Game sounds should be short — trim it to a few seconds.` });
      return;
    }
    setBusy({ slot: slot.id, progress: 0 });
    try {
      const ext = (file.name.match(/\.([a-z0-9]+)$/i)?.[1] || "mp3").toLowerCase();
      const path = `game-sounds/${scope}-${slot.id}-${Date.now()}-${safeName(file.name)}.${ext}`;
      const task = uploadBytesResumable(storageRef(storage, path), file, { contentType: file.type || "audio/mpeg" });
      await new Promise((resolve, reject) => {
        task.on("state_changed", (s) => setBusy({ slot: slot.id, progress: s.totalBytes ? s.bytesTransferred / s.totalBytes : 0 }), reject, resolve);
      });
      const url = await getDownloadURL(task.snapshot.ref);
      const prev = valueOf(slot.id);
      await write(slot.id, { url, path, name: file.name, volume: prev?.volume ?? 1 });
      playUrl(url, prev?.volume ?? 1);
      setMsg({ tone: "ok", text: `“${file.name}” now plays for “${slot.label}”${game ? ` in ${game.label}` : " in every game"}.` });
    } catch (e) {
      setMsg({ tone: "error", text: `Couldn’t upload: ${e?.message || e}` });
    } finally {
      setBusy(null);
    }
  };

  // count of uploaded / silenced slots per game, for the picker
  const countFor = (id) => Object.values(sounds.games?.[id] || {}).filter((v) => v?.url || v?.none).length;

  const groups = scope === "all" ? [["main", "Questions (every main game)"], ["bonus", "Bonus rounds (every bonus round)"]] : [[game?.group, game?.label]];

  return (
    <section className="lb-card gs-panel">
      <div className="lb-card-head">
        <span className="lb-step">🎵</span>
        <div>
          <h2>Game sounds</h2>
          <p className="lb-hint">
            Upload the sound for right answers, wrong answers, “good job”, game over and every bonus-round moment.
            Games pick them up straight away — no code change needed.
          </p>
        </div>
        <button type="button" className="lb-btn" onClick={() => setOpen((v) => !v)}>
          {open ? "Hide" : "Open"}
        </button>
      </div>

      {open && (
        <div className="gs-body">
          <div className="gs-scope">
            <button type="button" className={`gs-tab ${scope === "all" ? "is-on" : ""}`} onClick={() => setScope("all")}>
              Every game
            </button>
            <label className="gs-pick">
              One game only:
              <select value={scope === "all" ? "" : scope} onChange={(e) => setScope(e.target.value || "all")}>
                <option value="">— choose a game —</option>
                <optgroup label="Games">
                  {SOUND_GAMES.filter((g) => g.group === "main").map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.label}
                      {countFor(g.id) ? ` (${countFor(g.id)} own)` : ""}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Bonus rounds">
                  {SOUND_GAMES.filter((g) => g.group === "bonus").map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.label}
                      {countFor(g.id) ? ` (${countFor(g.id)} own)` : ""}
                    </option>
                  ))}
                </optgroup>
              </select>
            </label>
          </div>

          <p className="gs-hint">
            {scope === "all"
              ? "These play in every game. To give one game something different, choose it under “One game only”."
              : `Sounds just for ${game?.label}. Anything left as “Same as every game” uses the Every game sound.`}{" "}
            MP3 is best; keep sounds short (under 3 seconds for taps, a few seconds for “good job”), up to {MAX_MB} MB.
          </p>

          {msg && <div className={`gs-note is-${msg.tone}`}>{msg.text}</div>}

          {groups.map(([g, title]) => (
            <div key={g} className="gs-group">
              <h3>{title}</h3>
              {slots
                .filter((s) => s.group === g)
                .map((slot) => (
                  <SlotRow
                    key={`${scope}-${slot.id}`}
                    slot={slot}
                    scope={scope === "all" ? "all" : "game"}
                    value={valueOf(slot.id)}
                    inherited={inheritedOf(slot.id)}
                    busy={busy?.slot === slot.id}
                    progress={busy?.slot === slot.id ? busy.progress : 0}
                    onUpload={(f) => upload(slot, f)}
                    onVolume={(v) => save(slot.id, { ...valueOf(slot.id), volume: v })}
                    onNone={() => save(slot.id, { none: true }, `“${slot.label}” is now silent${game ? ` in ${game.label}` : ""}.`)}
                    onReset={() => save(slot.id, null, scope === "all" ? `“${slot.label}” is back to the built-in sound.` : `“${slot.label}” now uses the Every game sound.`)}
                  />
                ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default GameSoundsPanel;
